import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { detectEmbedProvider } from '../../../media/embedProviders';
import { getMediaSrc } from '../../../media/mediaSource';
import { registerChapterPlayer } from './videoChapterRegistry';
import VideoChapterNavigation from './VideoChapterNavigation';
import { VideoFrame, isMediaPresent } from '../../../media';

const JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' };
const ALIGN_ITEMS = { start: 'flex-start', center: 'center', end: 'flex-end' };

const placeholderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 120,
  width: '100%',
  border: '1px dashed var(--mantine-color-gray-4)',
  borderRadius: 'var(--mantine-radius-sm)',
  color: 'var(--mantine-color-gray-6)',
  fontSize: 13,
};

/**
 * Bloc Puck — Vidéo (fichier ou YouTube/Vimeo, sans options image).
 */
const VideoBlock = ({
  id,
  src = '',
  alignment = 'center',
  /* 'start' et non 'center' : sans réglage, le bloc doit rester posé en haut
     de sa colonne, comme avant que le champ existe. */
  alignVertical = 'start',
  width,
  mediaAspectRatio = 'auto',
  playback,
  automaticPoster = '',
  coverPoster = '',
  chapters = [],
  chapterLayout = 'list',
  chapterAccent = '',
  chapterTextStyles,
  previewChapters = false,
  puck,
}) => {
  const isEditing = !!puck?.isEditing;
  const source = getMediaSrc(src);
  const isFile = !!source && !detectEmbedProvider(source);
  const player = useMemo(() => ({ src: source, current: null }), [source]);
  const [position, setPosition] = useState({ currentTime: 0, duration: 0 });
  const onPlayerState = useCallback((state) => {
    player.current = state;
    setPosition((previous) => previous.currentTime === state.currentTime && previous.duration === state.duration
      ? previous : { currentTime: state.currentTime, duration: state.duration });
  }, [player]);
  useEffect(() => {
    if (!isEditing) return undefined;
    return registerChapterPlayer(id, source, player);
  }, [id, source, isEditing, player]);
  const playChapter = (chapter) => {
    if (!player.current || !position.duration) return;
    player.current.seek(chapter.time);
    player.current.play().catch(() => {});
  };

  const chapterNavigation = (
    <VideoChapterNavigation
      id={id}
      chapters={chapters}
      puck={puck}
      duration={position.duration}
      currentTime={position.currentTime}
      onPlayChapter={playChapter}
      layout={chapterLayout}
      accent={chapterAccent}
      textStyles={chapterTextStyles}
      preview={previewChapters}
    />
  );
  if (previewChapters) return chapterNavigation;

  if (!isMediaPresent(src)) {
    if (!isEditing) return null;
    return <div id={id} style={placeholderStyle}>Collez un lien vidéo dans le panneau de droite</div>;
  }

  return (
    /* `data-box-align` : la colonne étire la box, ce qui donne de la hauteur à
       son `align-items` — le marqueur que les autres box posent via
       ProposalBoxAlign (ProposalSection.module.css). */
    <div
      id={id}
      data-box-align=""
      style={{
        display: 'flex',
        justifyContent: JUSTIFY[alignment] || 'center',
        alignItems: ALIGN_ITEMS[alignVertical] || 'flex-start',
      }}
    >
      <div style={{ width: '100%', maxWidth: width ? `${width}px` : undefined }}>
        <VideoFrame
          key={source}
          src={src}
          onPlayerState={isFile && (isEditing || chapters.length > 0) ? onPlayerState : undefined}
          playback={playback}
          poster={coverPoster || automaticPoster}
          mediaFit="cover"
          mediaAspectRatio={mediaAspectRatio}
          isEditing={isEditing}
        />
        {isFile && chapterNavigation}
      </div>
    </div>
  );
};

export default VideoBlock;
